import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { importCSV, downloadCSVTemplate } from '../../../redux/actions/crmActions/CRMAction';
import { successNotification, errorNotification } from '../../../constants/Toast';
import './CSVImport.scss';

// eslint-disable-next-line react/prop-types
const CSVImport = ({ onImportComplete }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        errorNotification('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        errorNotification('Please select a CSV file');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      errorNotification('Please select a file first');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const result = await dispatch(importCSV(selectedFile));

      if (result.status === 'SUCCESS') {
        setImportResult(result.data);
        successNotification(
          `Successfully imported ${result.data.summary.successCount} leads!`
        );
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        // Notify parent to refresh the lead list
        if (onImportComplete) {
          onImportComplete();
        }
      } else {
        errorNotification(result.message || 'Import failed');
      }
    } catch (error) {
      errorNotification(error.message || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadCSVTemplate());
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="csv-import-container">
      <div className="csv-import-header">
        <h3>Import Leads from CSV</h3>
        <button
          type="button"
          className="button secondary-button"
          onClick={handleDownloadTemplate}
        >
          Download Template
        </button>
      </div>

      <div
        className={`csv-upload-area ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {!selectedFile ? (
          <div className="upload-placeholder">
            <div className="upload-icon">📂</div>
            <p className="upload-text">
              Drag & drop your CSV file here, or click to browse
            </p>
            <p className="upload-hint">
              Maximum file size: 5MB
            </p>
          </div>
        ) : (
          <div className="file-selected">
            <div className="file-icon">📄</div>
            <div className="file-info">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </p>
            </div>
          </div>
        )}
      </div>

      {selectedFile && (
        <div className="csv-import-actions">
          <button
            type="button"
            className="button secondary-button"
            onClick={handleCancel}
            disabled={importing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button primary-button"
            onClick={handleImport}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Import Leads'}
          </button>
        </div>
      )}

      {importResult && (
        <div className="import-result">
          <h4>Import Summary</h4>
          <div className="result-stats">
            <div className="stat success">
              <span className="stat-value">{importResult.summary.successCount}</span>
              <span className="stat-label">Successfully Imported</span>
            </div>
            <div className="stat total">
              <span className="stat-value">{importResult.summary.totalRows}</span>
              <span className="stat-label">Total Rows</span>
            </div>
            {importResult.summary.validationFailures > 0 && (
              <div className="stat error">
                <span className="stat-value">{importResult.summary.validationFailures}</span>
                <span className="stat-label">Validation Errors</span>
              </div>
            )}
            {importResult.summary.insertFailures > 0 && (
              <div className="stat error">
                <span className="stat-value">{importResult.summary.insertFailures}</span>
                <span className="stat-label">Insert Errors</span>
              </div>
            )}
          </div>

          {(importResult.validationErrors?.length > 0 || importResult.insertErrors?.length > 0) && (
            <div className="error-details">
              <h5>Error Details:</h5>
              <div className="error-list">
                {/* eslint-disable react/no-array-index-key */}
                {importResult.validationErrors?.map((err, idx) => (
                  <div key={`val-${idx}`} className="error-item">
                    <strong>Row {err.row}:</strong> {err.firstName} {err.lastName} -
                    {err.errors.join(', ')}
                  </div>
                ))}
                {importResult.insertErrors?.map((err, idx) => (
                  <div key={`ins-${idx}`} className="error-item">
                    <strong>Row {err.row}:</strong> {err.firstName} {err.lastName} -
                    {err.error}
                  </div>
                ))}
                {/* eslint-enable react/no-array-index-key */}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="csv-import-instructions">
        <h4>CSV Format Requirements:</h4>
        <ul>
          <li><strong>Required columns:</strong> first_name, last_name</li>
          <li><strong>Recommended columns:</strong> email, phone, company, title, location</li>
          <li><strong>Optional columns:</strong> stage, deal_size, likelihood, follow_up_date, notes, linkedin_url</li>
          <li><strong>Valid stages:</strong> POTENTIAL, INITIAL_CONTACT, IN_CONVERSION, MEETING_BOOKED, FOLLOW_UP, CLOSED, LOST</li>
          <li><strong>Valid likelihood:</strong> LIKELY, VERY_LIKELY, NOT_LIKELY</li>
        </ul>
      </div>
    </div>
  );
};

export default CSVImport;
